import React from "react";
import YouTube from "react-youtube";
import { IoPlay, IoStop } from "react-icons/io5";

import { playTimes } from "../../constants";

import * as Styled from "./index.styled";

interface Props {
  id: string;
  currentTry: number;
}

const { UNSTARTED, PLAYING, BUFFERING, ENDED } = YouTube.PlayerState;
const maxPlayTime = playTimes[playTimes.length - 1];

const opts = {
  // Not zero: iPhones won't load a video that takes up no space
  width: "160",
  height: "90",
  playerVars: {
    playsinline: 1, // stop iPhones opening the video full screen
    controls: 0,
    disablekb: 1,
  },
} as const;

// Add ?debug to the URL to see what the YouTube player is doing, e.g. on a phone
const debug = window.location.search.includes("debug");
const stateNames: Record<number, string> = {
  [-1]: "unstarted",
  0: "ended",
  1: "playing",
  2: "paused",
  3: "buffering",
  5: "cued",
};

export function Player({ id, currentTry }: Props) {
  const playerRef = React.useRef<YouTube>(null);
  const [isReady, setIsReady] = React.useState<boolean>(false);
  // What the player asked for, so the button responds straight away. The ref
  // copy is for YouTube's event handler, which outlives renders.
  const [playing, setPlaying] = React.useState<boolean>(false);
  const playingRef = React.useRef(false);
  // What YouTube reports, so timing starts only once audio is really playing
  const [playerState, setPlayerState] = React.useState<number>();
  const [currentTime, setCurrentTime] = React.useState<number>(0);
  const isPriming = React.useRef(false);
  const isPrimed = React.useRef(false);
  const primingBuffered = React.useRef(false);
  const primingTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  const [debugLog, setDebugLog] = React.useState<string[]>([]);
  const loadedAt = React.useRef(performance.now());

  const currentPlayTime = playTimes[currentTry];
  const internalPlayer = () => playerRef.current?.getInternalPlayer();

  // YouTube can report a last state change after the game screen is gone
  const isMounted = React.useRef(true);
  React.useEffect(
    () => () => {
      isMounted.current = false;
      clearTimeout(primingTimeout.current);
    },
    []
  );

  const log = (message: string) => {
    if (debug && isMounted.current) {
      const seconds = ((performance.now() - loadedAt.current) / 1000).toFixed(1);
      setDebugLog((lines) => [...lines.slice(-14), `${seconds}s ${message}`]);
    }
  };

  const updatePlaying = (value: boolean) => {
    playingRef.current = value;
    setPlaying(value);
  };

  const stop = React.useCallback(() => {
    log("stop");
    internalPlayer()?.pauseVideo();
    updatePlaying(false);
    setCurrentTime(0);
  }, []);

  // iPhones only start sound straight from a tap, and YouTube doesn't load the
  // video until it's first asked to play, so a first tap would only load it.
  // Load it ahead of time instead: play it muted, then pause and unmute it.
  const finishPriming = React.useCallback((loaded: boolean) => {
    if (!isPriming.current || !isMounted.current) {
      return;
    }
    log(loaded ? "loaded silently" : "silent load refused");
    isPriming.current = false;
    isPrimed.current = loaded;
    clearTimeout(primingTimeout.current);
    const player = internalPlayer();
    player?.pauseVideo();
    player?.seekTo(0, true);
    player?.unMute();
    setIsReady(true);
  }, []);

  const startPriming = React.useCallback(
    (reason: string) => {
      if (isPrimed.current || isPriming.current || !isMounted.current) {
        return;
      }
      log(`loading silently (${reason})`);
      isPriming.current = true;
      primingBuffered.current = false;
      const player = internalPlayer();
      player?.mute();
      player?.playVideo();
      // Browsers can refuse even muted playback; show the button anyway
      primingTimeout.current = setTimeout(() => finishPriming(false), 5000);
    },
    [finishPriming]
  );

  // If the silent load was refused, try again on the next tap anywhere, which
  // browsers allow. For new players that's closing the how-to-play popup.
  React.useEffect(() => {
    const retry = () => startPriming("tap");
    document.addEventListener("click", retry, true);
    return () => document.removeEventListener("click", retry, true);
  }, [startPriming]);

  const togglePlayback = React.useCallback(() => {
    if (playing) {
      stop();
      return;
    }
    log("play");
    if (isPriming.current) {
      // Tapped Play before the silent load finished: play for real instead
      isPriming.current = false;
      clearTimeout(primingTimeout.current);
    }
    // Always play from the start, even if something else paused it midway
    const player = internalPlayer();
    player?.unMute();
    player?.seekTo(0, true);
    player?.playVideo();
    updatePlaying(true);
  }, [playing, stop]);

  const onStateChange = React.useCallback(
    ({ data }: { data: number }) => {
      if (!isMounted.current) {
        return;
      }
      if (debug) {
        internalPlayer()
          ?.isMuted()
          .then((muted: boolean) =>
            log(`youtube: ${stateNames[data] ?? data}${muted ? " (muted)" : ""}`)
          );
      }
      setPlayerState(data);
      if (isPriming.current && data === BUFFERING) {
        primingBuffered.current = true;
      }
      // Dropping back to unstarted after buffering means the browser refused
      if (isPriming.current && data === UNSTARTED && primingBuffered.current) {
        finishPriming(false);
      }
      if (data === PLAYING) {
        if (isPriming.current) {
          finishPriming(true);
        } else if (!playingRef.current) {
          // Never let sound play that the player didn't ask for
          internalPlayer()?.pauseVideo();
        }
      }
      // The video can be shorter than the longest clip
      if (data === ENDED) {
        updatePlaying(false);
        setCurrentTime(0);
      }
    },
    [finishPriming]
  );

  // While audio plays, move the progress bar and stop at the unlocked length.
  // Timing restarts after any buffering, so a slow connection doesn't cut the
  // clip short.
  React.useEffect(() => {
    if (!playing || playerState !== PLAYING) {
      return;
    }
    let cancelled = false;
    let stopTimeout: ReturnType<typeof setTimeout>;
    let frame: number;
    playerRef.current
      ?.getInternalPlayer()
      .getCurrentTime()
      .then((startTime: number) => {
        if (cancelled) {
          return;
        }
        const startedAt = performance.now();
        stopTimeout = setTimeout(stop, currentPlayTime - startTime * 1000);
        const tick = () => {
          setCurrentTime(startTime + (performance.now() - startedAt) / 1000);
          // Setting state can run this effect's cleanup, so check again
          if (!cancelled) {
            frame = requestAnimationFrame(tick);
          }
        };
        tick();
      });
    return () => {
      cancelled = true;
      clearTimeout(stopTimeout);
      cancelAnimationFrame(frame);
    };
  }, [playing, playerState, currentPlayTime, stop]);

  const toPercent = (ms: number) => `${(ms / maxPlayTime) * 100}%`;
  const progress = Math.min(currentTime * 1000, currentPlayTime);

  return (
    <>
      <Styled.HiddenPlayer>
        <YouTube
          opts={opts}
          videoId={id}
          onReady={() => startPriming("page load")}
          onStateChange={onStateChange}
          ref={playerRef}
        />
      </Styled.HiddenPlayer>
      {debug && <Styled.DebugLog>{debugLog.join("\n")}</Styled.DebugLog>}
      {isReady ? (
        <>
          <Styled.ProgressBackground>
            {playing && <Styled.Progress style={{ width: toPercent(progress) }} />}
            {playTimes.map((playTime, i) => (
              <Styled.Separator
                style={{
                  left: toPercent(playTime),
                  backgroundColor: currentTry === i ? "green" : "gray",
                  width: currentTry === i ? "5px" : "1px",
                }}
                key={playTime}
              />
            ))}
          </Styled.ProgressBackground>
          <Styled.TimeStamps>
            {currentPlayTime !== maxPlayTime && (
              <Styled.TimeStamp style={{ left: toPercent(currentPlayTime) }}>
                {currentPlayTime / 1000}s
              </Styled.TimeStamp>
            )}
            <Styled.TimeStamp style={{ right: 0 }}>
              {maxPlayTime / 1000}s
            </Styled.TimeStamp>
          </Styled.TimeStamps>
          <Styled.PlayButton
            onClick={togglePlayback}
            aria-label={playing ? "Stop" : "Play"}
          >
            {playing ? <IoStop size={40} /> : <IoPlay size={40} />}
          </Styled.PlayButton>
        </>
      ) : (
        <p>Loading player...</p>
      )}
    </>
  );
}
